import { Component, OnInit, OnDestroy } from '@angular/core'
import { Firestore, collection, getDocs, query, orderBy, where } from '@angular/fire/firestore'
import { Subscription } from 'rxjs'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { BackendService } from 'src/app/services/backend.service'
import { EnterpriseContextService } from 'src/app/services/enterprise-context.service'
import { Merchant, merchantsPath, merchantParkingsPath, Parking } from '@parkupp/core'
import Swal from 'sweetalert2'

interface MerchantOption {
    $key: string
    companyName: string
    denormEnterpriseKey?: string
}

interface ParkingOption {
    $key: string
    title: string
    parkingStatus: string
    numberOfBays: number
}

interface MigrationPreview {
    parking: {
        key: string
        title: string
        status: string
        numberOfBays: number
    }
    sourceMerchant: {
        key: string
        companyName: string
    }
    targetMerchant: {
        key: string
        companyName: string
        denormEnterpriseKey?: string
    }
    subscriptions: {
        count: number
        items: Array<{
            key: string
            clientName: string
            status: string
        }>
    }
    invoices: {
        count: number
        totalValueInCents: number
    }
    payments: {
        count: number
    }
    bookings: {
        count: number
    }
    payouts: {
        count: number
    }
}

@Component({
    selector: 'app-admin-migrate-parking',
    templateUrl: './admin-migrate-parking.component.html',
    styleUrls: ['./admin-migrate-parking.component.scss'],
})
export class AdminMigrateParkingComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription[] = []

    // UI State
    loading = false
    loadingParkings = false
    loadingPreview = false
    migrating = false

    // Portfolio (Merchant) lists
    allPortfolios: MerchantOption[] = [] // All portfolios (unfiltered)
    portfolios: MerchantOption[] = [] // Filtered by enterprise
    targetPortfolios: MerchantOption[] = [] // Available target portfolios (excludes source)
    parkings: ParkingOption[] = []

    // Selection state
    selectedSourcePortfolio: string = ''
    selectedParking: string = ''
    selectedTargetPortfolio: string = ''

    // Preview data
    preview: MigrationPreview | null = null

    // Migration result
    migrationResult: {
        success: boolean
        message: string
        counts: {
            subscriptions: number
            invoices: number
            payments: number
            bookings: number
            payouts: number
        }
    } | null = null

    constructor(
        private firestore: Firestore,
        public authService: AuthenticationService,
        private backendService: BackendService,
        public enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        // Subscribe to enterprise context changes
        const entSub = this.enterpriseContext.currentEnterpriseKey$.subscribe(() => {
            this.filterPortfoliosByEnterprise()
            // Reset selections when enterprise changes
            this.resetForm()
        })
        this.subscriptions.push(entSub)

        // Initial load
        this.loadPortfolios()
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe())
    }

    async loadPortfolios(): Promise<void> {
        this.loading = true
        try {
            const merchantsRef = collection(this.firestore, merchantsPath())
            const q = query(merchantsRef, orderBy('companyName', 'asc'))
            const snapshot = await getDocs(q)
            // Filter out soft-deleted portfolios (those with deletedAt set)
            this.allPortfolios = snapshot.docs
                .filter((doc) => !doc.data()['deletedAt'])
                .map((doc) => ({
                    $key: doc.id,
                    companyName: doc.data()['companyName'] || 'Unnamed Portfolio',
                    denormEnterpriseKey: doc.data()['denormEnterpriseKey'],
                }))
            // Apply enterprise filter
            this.filterPortfoliosByEnterprise()
        } catch (error) {
            console.error('Error loading portfolios:', error)
            await Swal.fire('Error', 'Failed to load portfolios. Check console for details.', 'error')
        } finally {
            this.loading = false
        }
    }

    private filterPortfoliosByEnterprise(): void {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        if (enterpriseKey) {
            // Filter portfolios to only show those belonging to the selected enterprise
            this.portfolios = this.allPortfolios.filter((p) => p.denormEnterpriseKey === enterpriseKey)
        } else {
            // No enterprise selected - show all portfolios
            this.portfolios = [...this.allPortfolios]
        }
        // Update target portfolios list
        this.updateTargetPortfolios()
    }

    private updateTargetPortfolios(): void {
        // Filter out the source portfolio from target options
        if (this.selectedSourcePortfolio) {
            this.targetPortfolios = this.portfolios.filter((p) => p.$key !== this.selectedSourcePortfolio)
        } else {
            this.targetPortfolios = [...this.portfolios]
        }
    }

    async onSourcePortfolioChange(): Promise<void> {
        // Reset dependent selections
        this.selectedParking = ''
        this.selectedTargetPortfolio = ''
        this.parkings = []
        this.preview = null
        this.migrationResult = null

        // Update target portfolios list (excludes the selected source)
        this.updateTargetPortfolios()

        if (!this.selectedSourcePortfolio) {
            return
        }

        this.loadingParkings = true
        try {
            const parkingsRef = collection(this.firestore, merchantParkingsPath(this.selectedSourcePortfolio))
            const q = query(parkingsRef, orderBy('title', 'asc'))
            const snapshot = await getDocs(q)
            this.parkings = snapshot.docs.map((doc) => ({
                $key: doc.id,
                title: doc.data()['title'] || 'Untitled Parking',
                parkingStatus: doc.data()['parkingStatus'] || 'unknown',
                numberOfBays: doc.data()['numberOfBays'] || 0,
            }))
        } catch (error) {
            console.error('Error loading parkings:', error)
            await Swal.fire('Error', 'Failed to load parkings. Check console for details.', 'error')
        } finally {
            this.loadingParkings = false
        }
    }

    onParkingChange(): void {
        // Reset target selection and preview
        this.selectedTargetPortfolio = ''
        this.preview = null
        this.migrationResult = null
    }

    onTargetPortfolioChange(): void {
        // Reset preview
        this.preview = null
        this.migrationResult = null
    }

    get canLoadPreview(): boolean {
        return !!this.selectedSourcePortfolio && !!this.selectedParking && !!this.selectedTargetPortfolio
    }

    async loadPreview(): Promise<void> {
        if (!this.canLoadPreview) {
            return
        }

        this.loadingPreview = true
        this.preview = null
        this.migrationResult = null

        try {
            const idToken = await this.authService.auth.currentUser?.getIdToken()
            if (!idToken) throw new Error('No auth token')

            const result = await this.backendService.callFunction(
                'migrations-previewParkingMigration',
                {
                    parkingKey: this.selectedParking,
                    sourceMerchantKey: this.selectedSourcePortfolio,
                    targetMerchantKey: this.selectedTargetPortfolio,
                },
                idToken
            )

            this.preview = result
        } catch (error: any) {
            console.error('Error loading preview:', error)
            await Swal.fire('Error', error.message || 'Failed to load migration preview. Check console for details.', 'error')
        } finally {
            this.loadingPreview = false
        }
    }

    formatCurrency(cents: number): string {
        return 'R ' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    getStatusBadgeClass(status: string): string {
        const statusLower = status.toLowerCase()
        if (['active', 'published'].includes(statusLower)) {
            return 'bg-success'
        }
        if (['pending', 'new', 'paymentInitiated', 'renewing'].includes(statusLower)) {
            return 'bg-warning text-dark'
        }
        if (['expired', 'cancelled', 'rejected', 'disabled', 'bouncing', 'clientCancelled', 'merchantRejected'].includes(statusLower)) {
            return 'bg-danger'
        }
        if (['completed'].includes(statusLower)) {
            return 'bg-light text-dark'
        }
        return 'bg-secondary'
    }

    async executeMigration(): Promise<void> {
        if (!this.preview) {
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Confirm Migration',
            html: `
                <p>You are about to migrate:</p>
                <p class="fw-bold">${this.preview.parking.title}</p>
                <p>From: <strong>${this.preview.sourceMerchant.companyName}</strong></p>
                <p>To: <strong>${this.preview.targetMerchant.companyName}</strong></p>
                <hr>
                <p class="text-muted small">This will also migrate:</p>
                <ul class="text-start small">
                    <li>${this.preview.subscriptions.count} subscriptions</li>
                    <li>${this.preview.invoices.count} invoices</li>
                    <li>${this.preview.payments.count} payments</li>
                    <li>${this.preview.bookings.count} bookings</li>
                    <li>${this.preview.payouts.count} payouts</li>
                </ul>
                <p class="text-danger small"><i class="fa fa-warning"></i> This action cannot be easily undone!</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Migrate',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc3545',
        })

        if (!confirmResult.isConfirmed) {
            return
        }

        this.migrating = true
        this.migrationResult = null

        try {
            const idToken = await this.authService.auth.currentUser?.getIdToken()
            if (!idToken) throw new Error('No auth token')

            const result = await this.backendService.callFunction(
                'migrations-migrateParking',
                {
                    parkingKey: this.selectedParking,
                    sourceMerchantKey: this.selectedSourcePortfolio,
                    targetMerchantKey: this.selectedTargetPortfolio,
                },
                idToken
            )

            this.migrationResult = {
                success: result.success,
                message: result.message,
                counts: result.counts,
            }

            // Stop the loading state BEFORE showing the result dialog
            this.migrating = false

            if (result.success) {
                await Swal.fire({
                    title: 'Migration Complete!',
                    html: `
                        <p>${result.message}</p>
                        <hr>
                        <ul class="text-start">
                            <li><strong>${result.counts.subscriptions}</strong> subscriptions migrated</li>
                            <li><strong>${result.counts.invoices}</strong> invoices migrated</li>
                            <li><strong>${result.counts.payments}</strong> payments migrated</li>
                            <li><strong>${result.counts.bookings}</strong> bookings migrated</li>
                            <li><strong>${result.counts.payouts}</strong> payouts migrated</li>
                        </ul>
                    `,
                    icon: 'success',
                    confirmButtonText: 'OK',
                })

                // Reset form for next migration
                this.resetForm()
            } else {
                await Swal.fire('Migration Failed', result.message, 'error')
            }
        } catch (error: any) {
            console.error('Error executing migration:', error)
            // Stop the loading state BEFORE showing the error dialog
            this.migrating = false
            await Swal.fire('Error', error.message || 'Failed to execute migration. Check console for details.', 'error')
        }
    }

    resetForm(): void {
        this.selectedSourcePortfolio = ''
        this.selectedParking = ''
        this.selectedTargetPortfolio = ''
        this.parkings = []
        this.preview = null
        this.migrationResult = null
        this.updateTargetPortfolios()
    }
}
